import {BaseStore, globalStore} from "../../stem-core/state/Store";
import {apiClient, type MerchantConversionRequest} from "../connection/BlinkApiClient";
import {UserPreferences} from "./UserPreferencesStore";
import {Wallet} from "./WalletStore";
import {BaseEnum, makeEnum} from "../../stem-core/state/BaseEnum";
import {field} from "../../stem-core/state/StoreField";
import {BlinkGlobal} from "../../blinkpay/UtilsLib";
import {StemDate} from "../../stem-core/time/Date";
import {Jurisdiction} from "./JurisdictionStore";
import {Merchant} from "./MerchantStore";
import {Currency} from "../../stem-core/localization/CurrencyStore";
import type {StoreId} from "../../stem-core/state/State";

@makeEnum
export class UserType extends BaseEnum {
    static REGULAR;
    static MERCHANT;
    static ADMIN;
}

@globalStore
export class UserProfile extends BaseStore("UserProfile") {
    @field(UserType) userType: UserType;
    @field(String) email: string;
    @field(Boolean) isEmailVerified: boolean;
    @field(String) firstName: string;
    @field(String) lastName: string;
    @field(Boolean) isActive: boolean;
    @field(Date) createdAt: StemDate;
    @field(Date) lastLogin: StemDate;
    @field("Jurisdiction") jurisdiction: Jurisdiction;
    @field("Merchant") referralMerchant: Merchant;

    isRegular(): boolean {
        return this.userType === UserType.REGULAR;
    }

    isAdmin(): boolean {
        return this.userType === UserType.ADMIN;
    }

    getEmail(): string {
        return this.email;
    }

    getName(): string {
        const parts = [this.firstName, this.lastName].filter(x => x);
        return parts.join(" ");
    }

    getPreferences(): UserPreferences | undefined {
        return UserPreferences.find(obj => obj.userId === this.id);
    }

    getWallets(): Wallet[] {
        return Wallet.filter(item => item.userId === this.id);
    }

    getDefaultWallet(): Wallet | undefined {
        const defaultCurrencyId = this.getDefaultCurrency().id;
        return this.getWallets().find(w => w.currencyId === defaultCurrencyId);
    }

    getDefaultCurrency(): Currency {
        return this.getPreferences().getDefaultCurrency();
    }

    static getUserProfile(): UserProfile | undefined {
        // TODO @branch this doesn't work in the admin
        return this.get(BlinkGlobal.userId) || this.all()[0];
    }
}

// ConfirmCloseAccountRequest (api/users/views.py)
export interface ConfirmCloseAccountRequest {
    code: string;
}

export async function apiConfirmCloseAccount(data: ConfirmCloseAccountRequest): Promise<any> {
    return apiClient.post("/users/confirm_close_account", data);
}

// CloseAccountRequest
export interface CloseAccountRequest {
    reason: string;
}

export async function apiRequestCloseAccount(data: CloseAccountRequest): Promise<any> {
    return apiClient.post("/users/request_close_account", data);
}

// UpdateProfileRequest
export interface UpdateProfileRequest {
    firstName?: string;
    lastName?: string;
    requireSecureLoginCode?: boolean;
}

export async function apiUpdateUserProfile(data: UpdateProfileRequest): Promise<any> {
    return apiClient.post("/users/update_profile", data);
}

// ChangeEmailRequest, which extends MerchantConversionRequest
export interface ChangeEmailRequest extends MerchantConversionRequest {
    newEmail: string;
}

export async function apiRequestUserEmailChange(data: ChangeEmailRequest): Promise<any> {
    return apiClient.post("/users/request_email_change", data);
}

// ConfirmChangeEmailRequest, which extends MerchantConversionRequest
export interface ConfirmChangeEmailRequest extends MerchantConversionRequest {
    code: string;
}

export async function apiConfirmUserEmailChange(data: ConfirmChangeEmailRequest): Promise<any> {
    return apiClient.post("/users/confirm_email_change", data);
}

export async function apiUndoEmailChange(undoToken: string): Promise<any> {
    return apiClient.post("/users/undo_email_change", {undoToken});
}

export async function apiUndoCloseAccount(undoToken: string): Promise<any> {
    return apiClient.post("/users/cancel_close_account", {undoToken});
}

export async function apiUpdateUserMerchantActivity(merchantId: StoreId): Promise<any> {
    return apiClient.post("/users/update_merchant_activity", {merchantId});
}

// metadata is free-form on the backend too (EditMerchantUserMetadataRequest takes dict[str, Any])
export async function apiUserEditMerchantMetadata(merchantId: number, metadata: Record<string, unknown>): Promise<any> {
    return apiClient.post("/users/edit_merchant_user_metadata", {merchantId, metadata}, {allowNulls: true});
}

// What LogoutRequest validates (users/deserializers.py): only whether to end every session, plus the
// conversion fields it inherits
export interface LogoutRequest extends MerchantConversionRequest {
    allSessions?: boolean;
}

export async function apiLogoutUser(data: LogoutRequest = {}): Promise<any> {
    return apiClient.post("/users/logout", data);
}
